import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon, ToggleLeftIcon, ToggleRightIcon } from 'lucide-react';
import type { TaxType } from './TaxTypeManagement';
export function TaxTypeForm({
  taxType = null,
  onSave,
  onCancel,
  onToggleStatus,
  locale = 'en-US'
}) {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  useEffect(() => {
    if (taxType) {
      setFormData({
        code: taxType.code || '',
        name: taxType.name || '',
        description: taxType.description || ''
      });
    }
  }, [taxType]);
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const validateForm = () => {
    const newErrors = {};
    if (!formData.code) newErrors.code = 'Code is required';
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.description) newErrors.description = 'Description is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (validateForm()) {
      if (taxType) {
        onSave({
          ...taxType,
          ...formData
        });
      } else {
        onSave(formData);
      }
    }
  };
  const handleToggle = () => {
    if (taxType && onToggleStatus) {
      onToggleStatus(taxType.id);
      onCancel();
    }
  };
  return <form onSubmit={handleSubmit} className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-12441">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-12442">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12443">
          {taxType ? 'Edit Tax Type' : 'Create New Tax Type'}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-12444">
          {taxType ? 'Update the details of an existing tax type' : 'Enter the details to create a new tax type'}
        </p>
      </div>

      <div className="px-4 py-5 sm:p-6" data-id="element-12445">
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-12446">
          {/* Code */}
          <div className="sm:col-span-2" data-id="element-12447">
            <label htmlFor="code" className="block text-sm font-medium text-gray-700" data-id="element-12448">
              Code <span className="text-red-500" data-id="element-12449">*</span>
            </label>
            <div className="mt-1" data-id="element-12450">
              <input type="text" name="code" id="code" value={formData.code} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.code ? 'border-red-300' : ''}`} placeholder="e.g., GST, VAT" data-id="element-12451" />
              {errors.code && <p className="mt-1 text-sm text-red-600" data-id="element-12452">{errors.code}</p>}
            </div>
          </div>

          {/* Name */}
          <div className="sm:col-span-4" data-id="element-12453">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-12454">
              Name <span className="text-red-500" data-id="element-12455">*</span>
            </label>
            <div className="mt-1" data-id="element-12456">
              <input type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.name ? 'border-red-300' : ''}`} placeholder="e.g., Goods and Services Tax" data-id="element-12457" />
              {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-12458">{errors.name}</p>}
            </div>
          </div>

          {/* Description */}
          <div className="sm:col-span-6" data-id="element-12459">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-12460">
              Description <span className="text-red-500" data-id="element-12461">*</span>
            </label>
            <div className="mt-1" data-id="element-12462">
              <textarea name="description" id="description" rows={3} value={formData.description} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.description ? 'border-red-300' : ''}`} placeholder="Enter a detailed description of this tax type" data-id="element-12463" />
              {errors.description && <p className="mt-1 text-sm text-red-600" data-id="element-12464">
                  {errors.description}
                </p>}
            </div>
          </div>

          {/* Audit Information */}
          {taxType && <>
              <div className="sm:col-span-3" data-id="element-12465">
                <label className="block text-sm font-medium text-gray-700" data-id="element-12466">
                  Created By
                </label>
                <div className="mt-1" data-id="element-12467">
                  <input type="text" value={taxType.createdBy} disabled className="shadow-sm block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50" data-id="element-12468" />
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-12469">
                  {new Date(taxType.createdAt).toLocaleString(locale)}
                </p>
              </div>

              <div className="sm:col-span-3" data-id="element-12470">
                <label className="block text-sm font-medium text-gray-700" data-id="element-12471">
                  Last Modified By
                </label>
                <div className="mt-1" data-id="element-12472">
                  <input type="text" value={taxType.modifiedBy} disabled className="shadow-sm block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50" data-id="element-12473" />
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-12474">
                  {new Date(taxType.modifiedAt).toLocaleString(locale)}
                </p>
              </div>
            </>}
        </div>
      </div>

      <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-between items-center" data-id="element-12475">
        {/* Deactivate/Activate button */}
        <div data-id="element-12476">
          {taxType && onToggleStatus && <button type="button" onClick={handleToggle} className={`inline-flex items-center px-4 py-2 border shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${taxType.isActive ? 'text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-300 focus:ring-amber-500' : 'text-green-700 bg-green-50 hover:bg-green-100 border-green-300 focus:ring-green-500'}`} title={taxType.isActive ? 'Deactivate' : 'Activate'} data-id="element-12477">
              {taxType.isActive ? <>
                  <ToggleRightIcon className="h-4 w-4 mr-1.5" data-id="element-12478" />
                  Deactivate
                </> : <>
                  <ToggleLeftIcon className="h-4 w-4 mr-1.5" data-id="element-12479" />
                  Activate
                </>}
            </button>}
        </div>

        {/* Save and Cancel buttons */}
        <div className="flex space-x-3" data-id="element-12480">
          <button type="button" onClick={onCancel} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12481">
            <XIcon className="h-4 w-4 mr-1.5" data-id="element-12482" />
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12483">
            <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-12484" />
            {taxType ? 'Update Tax Type' : 'Create Tax Type'}
          </button>
        </div>
      </div>
    </form>;
}